import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../provider/AuthProvider";
import { useUser } from "../../provider/UserProvider";
import { EmployeeProfile } from "../../models/User";
import { LoadingSpinner } from "../../components/shared/ui/LoadingSpinner";
import { SubjectCombobox, StudyLevelMultiSelect } from "../../components/shared/ui";
import { ACADEMIC_ROLES } from "../../data/academicTaxonomy";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { uploadFile } from "../../services/uploadService";
import { acaPal, acaFont, AcaCTA, AcaIcon } from "../../components/shared/aca";
import { FolioSelect } from "../../components/shared/folio/Select";

const HE_RECOGNITION_OPTIONS = ["None", "Associate Fellow", "Fellow", "Senior Fellow"];
const HEARD_ABOUT_OPTIONS = ["LinkedIn", "Facebook", "Referral", "Instagram", "Other"];

const labelStyle: React.CSSProperties = {
  fontFamily: acaFont.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: acaPal.inkSoft,
  marginBottom: 6,
  display: "block",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "transparent",
  border: "none",
  borderBottom: `1.5px solid ${acaPal.ink}`,
  padding: "10px 0",
  fontFamily: acaFont.sans,
  fontSize: 16,
  color: acaPal.ink,
  outline: "none",
};

const sectionEbrow: React.CSSProperties = {
  fontFamily: acaFont.mono,
  fontSize: 10.5,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: acaPal.inkSoft,
  marginBottom: 8,
};

const h3Style: React.CSSProperties = {
  fontFamily: acaFont.sans,
  fontSize: 28,
  fontWeight: 600,
  letterSpacing: "-0.015em",
  margin: 0,
  color: acaPal.ink,
};

const EmployeeOnboardingPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { completeEmployeeOnboarding, error: onboardingError } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<EmployeeProfile>>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    address: "",
    role: "employee",
    specialization: "",
    academicRoles: [],
    studyLevels: [],
    eligibleToWorkInUK: false,
    phoneNumber: user?.phoneNumber || "",
  });

  const [cvFile, setCvFile] = useState<File | null>(null);
  const [degreeFile, setDegreeFile] = useState<File | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (file) {
      if (field === "cvUrl") setCvFile(file);
      if (field === "degreeUrl") setDegreeFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const profileData = { ...formData };
      if (cvFile) {
        const { url } = await uploadFile(cvFile, "cv");
        profileData.cvUrl = url;
      }
      if (degreeFile) {
        const { url } = await uploadFile(degreeFile, "cv");
        profileData.degreeUrl = url;
      }
      await completeEmployeeOnboarding(profileData as any);
      updateUser({
        onboardingCompleted: true,
        profileCompleted: true,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        cvUrl: profileData.cvUrl,
      });
      navigate(ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD);
    } catch (error) {
      console.error("Failed to complete profile:", error);
      alert(onboardingError || "Failed to complete profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== "employee") {
    return (
      <div style={{ background: acaPal.paper, minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", fontFamily: acaFont.sans }}>
          <div style={{ ...sectionEbrow, color: acaPal.accent }}>§ Access denied</div>
          <h1 style={{ ...h3Style, fontSize: 40 }}>This page is for scholars only.</h1>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: acaPal.paper, color: acaPal.ink, fontFamily: acaFont.sans }}>
      {/* Hero */}
      <section style={{ borderBottom: `1px solid ${acaPal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 20px 40px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 16,
              borderBottom: `1px solid ${acaPal.rule}`,
              paddingBottom: 14,
              fontFamily: acaFont.mono,
              fontSize: 10.5,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: acaPal.inkSoft,
              marginBottom: 32,
            }}
          >
            <div style={{ gridColumn: "span 4" }}>§ Onboarding · Scholar</div>
            <div style={{ gridColumn: "span 4", textAlign: "center" }}>Step 03 / 03</div>
            <div style={{ gridColumn: "span 4", textAlign: "right" }}>Editor reviews within 24h</div>
          </div>
          <div
            style={{
              fontFamily: acaFont.mono,
              fontSize: 11,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: acaPal.accent,
              marginBottom: 22,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ width: 28, height: 1, background: acaPal.accent }} />§ Complete your profile
          </div>
          <h1
            style={{
              fontFamily: acaFont.sans,
              fontSize: "clamp(48px, 7.5vw, 88px)",
              lineHeight: 0.94,
              letterSpacing: "-0.035em",
              fontWeight: 600,
              margin: 0,
              color: acaPal.ink,
              maxWidth: 1100,
            }}
          >
            Tell us who you are, and what you{" "}
            <span style={{ color: acaPal.accent, fontStyle: "italic", fontFamily: acaFont.serif, fontWeight: 400 }}>
              teach
            </span>
            .
          </h1>
          <p
            style={{
              fontFamily: acaFont.serif,
              fontSize: 20,
              lineHeight: 1.5,
              color: acaPal.inkSoft,
              maxWidth: 760,
              margin: "28px 0 0",
            }}
          >
            A short profile so the editorial desk can match you with positions worth your time.
          </p>
        </div>
      </section>

      {/* Form */}
      <section>
        <form onSubmit={handleSubmit} style={{ maxWidth: 880, margin: "0 auto", padding: "56px 20px 96px" }}>
          {/* Personal */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 01 · Personal information</div>
              <h3 style={h3Style}>Who you are.</h3>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
              <div>
                <label htmlFor="firstName" style={labelStyle}>First name *</label>
                <input id="firstName" name="firstName" type="text" value={formData.firstName} onChange={handleChange} style={inputStyle} required />
              </div>
              <div>
                <label htmlFor="lastName" style={labelStyle}>Last name *</label>
                <input id="lastName" name="lastName" type="text" value={formData.lastName} onChange={handleChange} style={inputStyle} required />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="address" style={labelStyle}>Address *</label>
                <textarea id="address" name="address" rows={3} value={formData.address} onChange={handleChange} style={{ ...inputStyle, resize: "vertical" }} required />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="phoneNumber" style={labelStyle}>Phone number</label>
                <input id="phoneNumber" name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={handleChange} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Professional */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 02 · Professional</div>
              <h3 style={h3Style}>Your field.</h3>
            </div>
            <div style={{ display: "grid", gap: 32 }}>
              <div>
                <label htmlFor="specialization" style={labelStyle}>Subject / specialization *</label>
                <SubjectCombobox
                  id="specialization"
                  name="specialization"
                  value={formData.specialization || ""}
                  onChange={(next) => setFormData((prev) => ({ ...prev, specialization: next }))}
                  placeholder="Search for your subject…"
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>Academic roles you're available for</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {ACADEMIC_ROLES.map((role) => {
                    const checked = (formData.academicRoles || []).includes(role);
                    return (
                      <label
                        key={role}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          border: `1px solid ${checked ? acaPal.ink : acaPal.rule}`,
                          background: checked ? acaPal.card : "transparent",
                          cursor: "pointer",
                          fontFamily: acaFont.sans,
                          fontSize: 14,
                          color: acaPal.ink,
                          transition: "all 120ms ease",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 18,
                            height: 18,
                            border: `1.5px solid ${acaPal.ink}`,
                            background: checked ? acaPal.ink : "transparent",
                            flexShrink: 0,
                          }}
                        >
                          {checked && <AcaIcon name="check" size={10} stroke={acaPal.paper} />}
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const current = formData.academicRoles || [];
                            const next = e.target.checked ? [...current, role] : current.filter((r) => r !== role);
                            setFormData((prev) => ({ ...prev, academicRoles: next }));
                          }}
                          style={{ display: "none" }}
                        />
                        <span>{role}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Study levels you can teach at</label>
                <StudyLevelMultiSelect
                  value={formData.studyLevels || []}
                  onChange={(next) => setFormData((prev) => ({ ...prev, studyLevels: next }))}
                />
              </div>

              <div>
                <label htmlFor="professionalAccreditation" style={labelStyle}>HE recognition</label>
                <FolioSelect
                  id="professionalAccreditation"
                  name="professionalAccreditation"
                  placeholder="Select your accreditation"
                  value={formData.professionalAccreditation || ''}
                  onChange={(v) => setFormData((prev) => ({ ...prev, professionalAccreditation: v }))}
                  options={HE_RECOGNITION_OPTIONS}
                />
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  fontFamily: acaFont.sans,
                  fontSize: 14,
                  color: acaPal.ink,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    border: `1.5px solid ${acaPal.ink}`,
                    background: formData.eligibleToWorkInUK ? acaPal.ink : "transparent",
                  }}
                >
                  {formData.eligibleToWorkInUK && <AcaIcon name="check" size={10} stroke={acaPal.paper} />}
                </span>
                <input
                  type="checkbox"
                  id="eligibleToWorkInUK"
                  name="eligibleToWorkInUK"
                  checked={formData.eligibleToWorkInUK}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
                <span>I am eligible to work in the UK</span>
              </label>
            </div>
          </div>

          {/* Documents */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 03 · Documents</div>
              <h3 style={h3Style}>Attach your record.</h3>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
              <div>
                <label htmlFor="cvUrl" style={labelStyle}>CV / Resume</label>
                <input id="cvUrl" type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, "cvUrl")} style={{ ...inputStyle, padding: "8px 0" }} />
                {cvFile && <p style={{ fontFamily: acaFont.mono, fontSize: 11, color: acaPal.inkSoft, marginTop: 6, letterSpacing: "0.04em" }}>{cvFile.name}</p>}
              </div>
              <div>
                <label htmlFor="degreeUrl" style={labelStyle}>Degree certificate</label>
                <input id="degreeUrl" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange(e, "degreeUrl")} style={{ ...inputStyle, padding: "8px 0" }} />
                {degreeFile && <p style={{ fontFamily: acaFont.mono, fontSize: 11, color: acaPal.inkSoft, marginTop: 6, letterSpacing: "0.04em" }}>{degreeFile.name}</p>}
              </div>
              <div>
                <label htmlFor="dbsCertificateNumber" style={labelStyle}>DBS certificate number</label>
                <input id="dbsCertificateNumber" name="dbsCertificateNumber" type="text" value={formData.dbsCertificateNumber} onChange={handleChange} style={inputStyle} />
              </div>
              <div>
                <label htmlFor="dbsExpiryDate" style={labelStyle}>DBS expiry date</label>
                <input id="dbsExpiryDate" name="dbsExpiryDate" type="date" value={formData.dbsExpiryDate} onChange={handleChange} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Additional */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ borderBottom: `1px solid ${acaPal.rule}`, paddingBottom: 14, marginBottom: 28 }}>
              <div style={sectionEbrow}>§ 04 · Additional</div>
              <h3 style={h3Style}>One last note.</h3>
            </div>
            <div>
              <label htmlFor="heardAboutUs" style={labelStyle}>How did you hear about us?</label>
              <FolioSelect
                id="heardAboutUs"
                name="heardAboutUs"
                placeholder="Select an option"
                value={formData.heardAboutUs || ''}
                onChange={(v) => setFormData((prev) => ({ ...prev, heardAboutUs: v }))}
                options={HEARD_ABOUT_OPTIONS}
              />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", paddingTop: 24, borderTop: `1px solid ${acaPal.rule}`, flexWrap: "wrap" }}>
            <AcaCTA kind="outline" size="md" onClick={() => navigate(ROUTE_NAMES.PAGES.HOME)} type="button">
              Skip for now
            </AcaCTA>
            <AcaCTA kind="accent" size="md" type="submit" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" color="white" /> : "Complete profile"}
            </AcaCTA>
          </div>
        </form>
      </section>
    </div>
  );
};

export default EmployeeOnboardingPage;
